/**
 * @file Renders a dropdown menu with an inline field for adding a new option to it.
 */
import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { PlusOutlined } from "@ant-design/icons";
import { Divider, Input, Row, Col, Button } from "antd";
import type { CSSProperties, ChangeEvent, KeyboardEvent, MouseEvent, ReactNode } from "react";

import { W1 } from "@components/Constants";

const LABEL_ADD = "Add";
const LABEL_PLACEHOLDER = "Enter subscription type name...";

const DIVIDER_STYLE: CSSProperties = { margin: "8px 0" };
const ROW_STYLE: CSSProperties = { padding: "0 0 4px" };

type OutletContext = {
  ensureOption: (group: string, name: string) => Promise<unknown>;
};

type Props = {
  menu: ReactNode;
  group: string;
};

/**
 * Renders the given menu with a field and a button for adding a new option to the group.
 */
const OptionsDropdown = ({ menu, group }: Props): JSX.Element => {
  const { ensureOption } = useOutletContext<OutletContext>();

  const [ name, setName ] = useState("");
  const [ isLoading, setIsLoading ] = useState(false);

  const isDisabled = name === "";

  /**
   * Stores the value the user typed for the new option's name.
   */
  const onNameChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const { value = "" } = event.target;
    setName(value);
  };

  /**
   * Stops a key press in the field from also acting on the dropdown menu.
   */
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => event.stopPropagation();

  /**
   * Adds the typed name as a new option of the group, and clears the field.
   */
  const onClick = async (event: MouseEvent<HTMLElement>): Promise<void> => {
    event.preventDefault();

    const isEmpty = name === "";

    if (isEmpty) {
      return;
    }

    setIsLoading(true);

    await ensureOption(group, name);

    setName("");
    setIsLoading(false);
  };

  return (
    <>
      {menu}

      <Divider style={DIVIDER_STYLE} />

      <Row
        style={ROW_STYLE}
        gutter={W1}
      >
        <Col flex="auto">
          <Input
            value={name}
            variant="borderless"
            onChange={onNameChange}
            onKeyDown={onKeyDown}
            placeholder={LABEL_PLACEHOLDER}
          />
        </Col>

        <Col>
          <Button
            type="text"
            icon={<PlusOutlined />}
            loading={isLoading}
            onClick={onClick}
            disabled={isDisabled}
          >
            {LABEL_ADD}
          </Button>
        </Col>
      </Row>
    </>
  );
};

export default OptionsDropdown;
